import { useState } from "react";
import { Apribile, Badge, Button, Card, Empty, ErrorNotice, Spinner } from "../components/ui.tsx";
import { useQuery } from "../lib/hooks.ts";
import { AZIONI_NOTE, fraseAttivita } from "../lib/attivita.ts";
import { useT } from "../lib/i18n.tsx";
import { linkProps } from "../lib/router.ts";
import type { AuditEntry, StackConRiepilogo } from "../lib/types.ts";
import { formatoData } from "./Stacks.tsx";
import { dataSqlite } from "../lib/stato-stack.ts";

const DIMENSIONE = 50;
const CLASSE_SELECT = "rounded-lg border bg-[var(--superficie)] px-3 py-2 text-sm";

/**
 * Attività: chi ha fatto cosa, in frasi.
 *
 * In sola lettura, come il registro stesso; include le operazioni avviate dai
 * webhook, che non hanno un utente. Il codice dell'azione e il JSON restano
 * dietro «dettagli».
 */
export function AuditPage() {
  const t = useT();
  const [pagina, setPagina] = useState(0);
  const [azione, setAzione] = useState("");
  const [stack, setStack] = useState("");
  const [attore, setAttore] = useState("");

  const stacks = useQuery<{ stacks: StackConRiepilogo[] }>("/stacks");
  const attori = useQuery<{ attori: string[] }>("/audit/attori");

  const filtri = new URLSearchParams({ limit: String(DIMENSIONE), offset: String(pagina * DIMENSIONE) });
  if (azione) filtri.set("action", azione);
  if (stack) {
    filtri.set("resourceType", "stack");
    filtri.set("resourceId", stack);
  }
  if (attore) filtri.set("actor", attore);

  const { data, error, loading } = useQuery<{ entries: AuditEntry[]; total: number }>(`/audit?${filtri}`);
  const ultima = data === null || (pagina + 1) * DIMENSIONE >= data.total;

  // Un filtro cambiato riporta alla prima pagina.
  const filtro = (imposta: (v: string) => void) => (e: { target: { value: string } }) => {
    imposta(e.target.value);
    setPagina(0);
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold">{t.t("attivita.titolo")}</h1>
        <p className="text-sm tenue">{t.t("attivita.intro")}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <select className={CLASSE_SELECT} aria-label={t.t("attivita.filtroAzione")} value={azione} onChange={filtro(setAzione)}>
          <option value="">{t.t("attivita.tutteAzioni")}</option>
          {AZIONI_NOTE.map((a) => (
            <option key={a} value={a}>
              {t.t(`attivita.filtro.${a}` as "attivita.filtro.stack.deploy")}
            </option>
          ))}
        </select>

        <select className={CLASSE_SELECT} aria-label={t.t("attivita.filtroStack")} value={stack} onChange={filtro(setStack)}>
          <option value="">{t.t("attivita.tuttiStack")}</option>
          {stacks.data?.stacks.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>

        <select className={CLASSE_SELECT} aria-label={t.t("attivita.filtroUtente")} value={attore} onChange={filtro(setAttore)}>
          <option value="">{t.t("attivita.tuttiUtenti")}</option>
          {attori.data?.attori.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </div>

      <ErrorNotice error={error} />
      {loading && <Spinner className="tenue" />}

      {data && data.entries.length === 0 && <Empty title={t.t("attivita.vuoto")} />}

      {data && data.entries.length > 0 && (
        <>
          <Card className="divide-y overflow-hidden">
            {data.entries.map((voce) => (
              <RigaAttivita key={voce.id} voce={voce} />
            ))}
          </Card>

          <div className="flex items-center justify-between text-sm">
            <span className="tenue">
              {t.t("attivita.intervallo", {
                da: pagina * DIMENSIONE + 1,
                a: pagina * DIMENSIONE + data.entries.length,
                totale: data.total,
              })}
            </span>

            <div className="flex gap-2">
              <Button disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>
                {t.t("attivita.precedenti")}
              </Button>
              <Button disabled={ultima} onClick={() => setPagina((p) => p + 1)}>
                {t.t("attivita.successive")}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function RigaAttivita({ voce }: { voce: AuditEntry }) {
  const t = useT();

  return (
    <div className="space-y-2 px-4 py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="w-36 shrink-0 text-xs tenue" title={t.tempoFa(dataSqlite(voce.createdAt))}>
          {formatoData(voce.createdAt, t)}
        </span>

        <span>
          {fraseAttivita(voce, t).map((s, i) =>
            typeof s === "string" ? (
              <span key={i}>{s}</span>
            ) : (
              <a key={i} {...linkProps(`/stacks/${s.id}`)} className="font-medium underline">
                {s.stack}
              </a>
            ),
          )}
        </span>

        {voce.outcome === "failure" && <Badge tone="danger">✗ {t.t("attivita.fallita")}</Badge>}

        {voce.ipAddress && (
          <code className="ml-auto font-mono text-xs tenue" title={t.t("attivita.ip")}>
            {voce.ipAddress}
          </code>
        )}
      </div>

      {voce.details !== null && voce.details !== undefined && (
        <Apribile titolo={t.t("attivita.dettagli")}>
          <pre className="overflow-x-auto text-xs">{JSON.stringify(voce.details, null, 2)}</pre>
        </Apribile>
      )}
    </div>
  );
}
